import { useState } from "react";
import { toast } from "sonner";
import { Ban, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Loader } from "../components/common";
import { DocSheet } from "../components/DocSheet";
import { DocToolbar, useDocParams } from "../components/DocToolbar";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, dateTimeFmt } from "../lib/api";

function CancelReceipt({ sale, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/cancel`, { reason });
      toast.success(`${sale.receipt_no} cancelled · items returned to stock`);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-rose-700 text-rose-400 hover:bg-rose-500/10" data-testid="cancel-receipt-button"><Ban className="h-4 w-4" /> Cancel receipt</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel {sale.receipt_no}?</DialogTitle>
          <DialogDescription>Every item goes back into {sale.depot_name} stock and the sale is removed from cashflow{sale.payment_method === "Credit" ? " and the customer's debt" : ""}. The receipt stays in the records marked CANCELLED.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for cancelling (required)" className="bg-slate-950" data-testid="cancel-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-receipt-dismiss">Keep receipt</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="cancel-receipt-confirm">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Cancel receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SaleDoc() {
  const { id, thermal, setThermal } = useDocParams();
  const { isAdmin } = useAuth();
  const { data: s, reload } = useApi(`/sales/${id}`);
  if (!s) return <Loader />;
  const credit = s.payment_method === "Credit";
  const kind = credit ? "INVOICE" : "RECEIPT";
  const cancelled = s.status === "cancelled";
  const stamp = cancelled ? `Cancelled ${dateTimeFmt(s.cancelled_at)} by ${s.cancelled_by} · Reason: ${s.cancel_reason}` : "";
  const extra = cancelled ? <span className="text-sm font-semibold text-rose-400" data-testid="receipt-cancelled-label">CANCELLED</span>
    : isAdmin ? <CancelReceipt sale={s} onDone={reload} /> : null;
  return (
    <div className="space-y-6">
      <DocToolbar title={`${kind === "INVOICE" ? "Invoice" : "Receipt"} ${s.receipt_no}`} pdfPath={`/sales/${id}/pdf`} filename={`${kind.toLowerCase()}-${s.receipt_no}.pdf`} thermal={thermal} setThermal={setThermal} extra={extra} />
      <DocSheet kind={kind} number={s.receipt_no} thermal={thermal} items={s.items} total={s.total} stamp={stamp}
        meta={[["Date", dateTimeFmt(s.created_at)], ["Depot", s.depot_name], ["Customer", s.customer], ["Phone", s.customer_phone], ["Payment", s.payment_method], ["Served by", s.user_name]]}
        footer={[credit ? "Amount on credit - please pay at the depot." : "Paid. Thank you for your business!", "Prices include VAT at 18%.", "Goods once sold are not returnable without this receipt."]} />
    </div>
  );
}
